// P41 (Q5, RELEASE-TENIS10 § 11): an administrator withdraws the entry of a child not claimed yet — held by its tenis10.ro
// family, entered paid on site — through the SportyaOS withdrawal behind an administrator route (the family has no account,
// so the administrative entry operation has no payer to work with). First the money outcome (the withdrawal preview: what
// the club still owes Sportya is voided, an unused passport the entry activated is reverted), then the withdrawal; the result
// comes on the socket, matched by the request's correlation id. P45: worded as SportyaOS words it since 23.10 (nothing fronted).
import React, { useContext, useEffect, useRef, useState } from "react"
import { Button, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "reactstrap"
import { del, get } from "helpers/api_helper"
import { store as socketStore } from "components/Common/Socket/store"
import { createEventOperationId } from "../../../../../eventOperationId"
import { tenis10EntryText } from "./Tenis10ChildEntryModal"

const RESULT_TIMEOUT_MS = 30000
const entriesPath = (eventId, competitionId) => `/admin/tenis10/events/${eventId}/competitions/${competitionId}/entries`

export const readChildWithdrawalPreview = ({ eventId, competitionId, entryId }) =>
  get(`${entriesPath(eventId, competitionId)}/${entryId}/withdrawal-preview`)
export const withdrawChild = ({ eventId, competitionId, entryId, socketClientId, correlationId }) =>
  del(`${entriesPath(eventId, competitionId)}/${entryId}`, { socketClientId }, { headers: { "X-Correlation-Id": correlationId } })

const money = ({ amount, currency } = {}) => `${Number(amount) || 0} ${currency || "RON"}`

/** The money lines of the outcome, in words: nothing was paid online, so nothing is refunded. */
export const outcomeLines = (outcome = {}) => {
  const lines = ["Nothing was paid online, so nothing is refunded."]
  if (outcome.kind === "override" && outcome.ledger) lines.push("The amount for Sportya for this entry is cancelled.")
  if (outcome.passport?.outcome === "void_reverted") lines.push(`The passport activated with this entry (${money(outcome.passport)}) is deactivated.`)
  else if (outcome.passport) lines.push("The passport stays active: it was used or already settled.")
  return lines
}

const Tenis10ChildWithdrawModal = ({ entry, eventId, competitionId, onClose, onWithdrawn }) => {
  const { state: { socket: { socket, socketClientId } = {} } = {} } = useContext(socketStore)
  const [preview, setPreview] = useState({ data: null, loading: false, error: null })
  const [submit, setSubmit] = useState({ pending: false, error: null })
  const pending = useRef(null)
  const withdrawn = useRef(onWithdrawn)
  withdrawn.current = onWithdrawn
  const entryId = entry?._id
  const name = [entry?.profile?.firstName, entry?.profile?.lastName].filter(Boolean).join(" ") || "this child"

  useEffect(() => {
    if (!entryId) return
    setPreview({ data: null, loading: true, error: null })
    setSubmit({ pending: false, error: null })
    // the preview of THIS entry only: an answer for an entry closed meanwhile is dropped
    let current = true
    readChildWithdrawalPreview({ eventId, competitionId, entryId })
      .then(response => { if (current) setPreview({ data: response?.data ?? null, loading: false, error: null }) })
      .catch(error => { if (current) setPreview({ data: null, loading: false, error: tenis10EntryText(error?.response?.data?.message || "ENTRY_PREVIEW_UNAVAILABLE") }) })
    return () => { current = false }
  }, [entryId, eventId, competitionId])

  // the outcome on the socket, listened to only while a withdrawal is open
  useEffect(() => {
    if (!socket || !entryId) return undefined
    const onResponse = payload => {
      if (!pending.current || payload?.correlationId !== pending.current.correlationId) return
      clearTimeout(pending.current.timer)
      pending.current = null
      if (payload.success === true) return withdrawn.current?.("The entry was withdrawn.", "success")
      return setSubmit({ pending: false, error: tenis10EntryText(payload.message) })
    }
    socket.on("events-response", onResponse)
    socket.on("payments-response", onResponse)
    return () => {
      socket.off("events-response", onResponse)
      socket.off("payments-response", onResponse)
    }
  }, [socket, entryId])

  const confirm = async () => {
    if (submit.pending || !preview.data) return
    const correlationId = createEventOperationId("tenis10-child-withdrawal")
    pending.current = { correlationId, timer: setTimeout(() => { pending.current = null; withdrawn.current?.("The result is not confirmed yet — check the entries list.", "warning") }, RESULT_TIMEOUT_MS) }
    setSubmit({ pending: true, error: null })
    try {
      await withdrawChild({ eventId, competitionId, entryId, socketClientId, correlationId })
    } catch (error) {
      clearTimeout(pending.current?.timer)
      pending.current = null
      setSubmit({ pending: false, error: tenis10EntryText(error?.response?.data?.message) })
    }
  }

  const close = submit.pending ? undefined : onClose
  return <Modal isOpen={Boolean(entryId)} toggle={close}>
    <ModalHeader toggle={close}>Withdraw {name}</ModalHeader>
    <ModalBody>
      <p>The child is not claimed yet: the entry is held by its tenis10.ro family. Nobody is notified.</p>
      {preview.loading && <p className="text-muted"><Spinner size="sm" /> Checking the money…</p>}
      {preview.error && <div className="alert alert-danger" role="alert">{preview.error}</div>}
      {preview.data && <ul data-testid="withdraw-outcome">{outcomeLines(preview.data).map(line => <li key={line}>{line}</li>)}</ul>}
      {submit.error && <div className="alert alert-danger" role="alert">{submit.error}</div>}
    </ModalBody>
    <ModalFooter>
      <Button color="secondary" onClick={onClose} disabled={submit.pending}>Cancel</Button>
      <Button color="danger" disabled={!preview.data || submit.pending} onClick={confirm}>
        {submit.pending ? <><Spinner size="sm" /> Withdrawing…</> : "Withdraw"}
      </Button>
    </ModalFooter>
  </Modal>
}

export default Tenis10ChildWithdrawModal
